import { type ChangeEvent, type FormEvent, useState } from 'react';
import useChangePassword, { ChangePasswordError } from '../../api/useChangePassword';

export const MIN_PASSWORD_LENGTH = 8;

type Props = {
  email: string;
};

type Fields = {
  currentPassword: string;
  newPassword: string;
  confirmation: string;
};

type FieldErrors = Partial<Record<keyof Fields, string>>;

const EMPTY_FIELDS: Fields = { currentPassword: '', newPassword: '', confirmation: '' };

export function validatePasswordChange(fields: Fields): FieldErrors {
  const errors: FieldErrors = {};

  if (fields.currentPassword === '') {
    errors.currentPassword = 'Saisissez votre mot de passe actuel.';
  }
  if (fields.newPassword.length < MIN_PASSWORD_LENGTH) {
    errors.newPassword = `Au moins ${MIN_PASSWORD_LENGTH} caractères.`;
  } else if (fields.newPassword === fields.currentPassword) {
    errors.newPassword = "Choisissez un mot de passe différent de l'actuel.";
  }
  if (fields.confirmation !== fields.newPassword) {
    errors.confirmation = 'Les deux mots de passe ne correspondent pas.';
  }

  return errors;
}

export default function useChangePasswordCard(props: Props) {
  const changePassword = useChangePassword();
  const [fields, setFields] = useState<Fields>(EMPTY_FIELDS);
  // Errors only show once the user tried to submit, then follow every keystroke.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const validationErrors = hasSubmitted ? validatePasswordChange(fields) : {};
  const errors: FieldErrors = { ...serverErrors, ...validationErrors };

  function handleFieldChange(field: keyof Fields) {
    return function handleChange(event: ChangeEvent<HTMLInputElement>) {
      setFields((previous) => ({ ...previous, [field]: event.target.value }));
      setServerErrors((previous) => ({ ...previous, [field]: undefined }));
    };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setHasSubmitted(true);
    setServerError(null);
    setServerErrors({});

    if (Object.keys(validatePasswordChange(fields)).length > 0) return;

    try {
      await changePassword.mutateAsync({
        email: props.email,
        currentPassword: fields.currentPassword,
        newPassword: fields.newPassword,
      });
      setFields(EMPTY_FIELDS);
      setHasSubmitted(false);
    } catch (error: unknown) {
      const reason = error instanceof ChangePasswordError ? error.reason : 'unknown';
      if (reason === 'wrong-current-password') {
        setServerErrors({ currentPassword: 'Mot de passe actuel incorrect.' });
      } else if (reason === 'same-password') {
        setServerErrors({ newPassword: "Choisissez un mot de passe différent de l'actuel." });
      } else if (reason === 'weak-password') {
        setServerErrors({ newPassword: 'Mot de passe trop faible : mélangez lettres, chiffres et symboles.' });
      } else {
        setServerError("Le mot de passe n'a pas pu être modifié. Réessayez dans un instant.");
      }
    }
  }

  return {
    fields,
    errors,
    serverError,
    canSubmit: fields.currentPassword !== '' && fields.newPassword !== '' && fields.confirmation !== '',
    isSaving: changePassword.isPending,
    handleFieldChange,
    handleSubmit,
  };
}
